import React, { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getScoreColors } from '../utils/colorUtils';
import MatrixVisualization from '../components/MatrixVisualization';

interface Problem {
  id: string;
  title: string;
  description: string;
  impact_area: string;
  priority: number;
  project_id?: string;
}

interface Hypothesis {
  id: string;
  title: string;
  hypothesis_statement: string;
  business_impact: number;
  technical_feasibility: number;
  strategy_alignment: number;
  implementation_effort: number;
  priority_score: number;
  problem_id: string;
  project_id: string;
  ai_capability: string;
  expected_outcome: string;
  testing_requirements: string;
  metrics_to_track: string[];
}

interface Project {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'completed' | 'on-hold' | 'archived';
}

const MatrixPage: React.FC = () => {
  const { get } = useApi();
  const { socket, isConnected } = useWebSocket();
  const [hypotheses, setHypotheses] = useState<Hypothesis[]>([]);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterProject, setFilterProject] = useState<string>('All');
  const [filterProblem, setFilterProblem] = useState<string>('All');
  const [yAxis, setYAxis] = useState<string>('business_impact');
  const [xAxis, setXAxis] = useState<string>('implementation_effort');
  const [problemsArray, setProblemsArray] = useState<Problem[]>([]);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [hypothesesData, problemsData, projectsData] = await Promise.all([
          get<Hypothesis[]>('/hypotheses'),
          get<Problem[]>('/problems'),
          get<Project[]>('/projects')
        ]);
        
        setHypotheses(Array.isArray(hypothesesData) ? hypothesesData : []);
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        
        // Store problems array and create problems lookup
        const problemsArr = Array.isArray(problemsData) ? problemsData : [];
        setProblemsArray(problemsArr);
        
        const problemsLookup: Record<string, string> = {};
        problemsArr.forEach(p => {
          problemsLookup[p.id] = p.title;
        });
        setProblems(problemsLookup);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [get]);

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleHypothesisUpdated = (data: { hypothesis: Hypothesis }) => {
      setHypotheses(prev => 
        prev.map(h => h.id === data.hypothesis.id ? data.hypothesis : h)
      );
    };

    const handleHypothesisCreated = (data: { hypothesis: Hypothesis }) => {
      setHypotheses(prev => [...prev, data.hypothesis]);
    };

    const handleHypothesisDeleted = (data: { hypothesis_id: string }) => {
      setHypotheses(prev => prev.filter(h => h.id !== data.hypothesis_id));
    };

    const handleProblemUpdated = (data: { problem: Problem }) => {
      setProblems(prev => ({
        ...prev,
        [data.problem.id]: data.problem.title
      }));
    };

    const handleProblemCreated = (data: { problem: Problem }) => {
      setProblems(prev => ({
        ...prev,
        [data.problem.id]: data.problem.title
      }));
    };

    const handleProblemDeleted = (data: { problem_id: string }) => {
      setProblems(prev => {
        const newProblems = { ...prev };
        delete newProblems[data.problem_id];
        return newProblems;
      });
    };

    socket.on('hypothesis_updated', handleHypothesisUpdated);
    socket.on('hypothesis_created', handleHypothesisCreated);
    socket.on('hypothesis_deleted', handleHypothesisDeleted);
    socket.on('problem_updated', handleProblemUpdated);
    socket.on('problem_created', handleProblemCreated);
    socket.on('problem_deleted', handleProblemDeleted);

    return () => {
      socket.off('hypothesis_updated', handleHypothesisUpdated);
      socket.off('hypothesis_created', handleHypothesisCreated);
      socket.off('hypothesis_deleted', handleHypothesisDeleted);
      socket.off('problem_updated', handleProblemUpdated);
      socket.off('problem_created', handleProblemCreated);
      socket.off('problem_deleted', handleProblemDeleted);
    };
  }, [socket, isConnected]);

  // Filter hypotheses by project and problem
  let filteredHypotheses = hypotheses;
  
  // First filter by project if selected
  if (filterProject !== 'All') {
    filteredHypotheses = filteredHypotheses.filter(h => h.project_id === filterProject);
  }
  
  // Then filter by problem if selected
  if (filterProblem !== 'All') {
    filteredHypotheses = filteredHypotheses.filter(h => h.problem_id === filterProblem);
  }
  
  // Filter problems for dropdown based on selected project
  const availableProblems = filterProject === 'All' 
    ? problems 
    : Object.fromEntries(
        Object.entries(problems).filter(([problemId]) => {
          const problem = problemsArray.find(p => p.id === problemId);
          return problem && problem.project_id === filterProject;
        })
      );
  
  const focusProblemId = filterProblem === 'All' ? undefined : filterProblem;




  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading matrix data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        <p className="font-medium">Error loading matrix data</p>
        <p className="mt-1 text-sm">{error}</p>
      </div>
    );
  }


  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">AI Opportunity Prioritization Matrix</h1>
      
      {hypotheses.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <p className="text-gray-500">No AI hypotheses available for visualization. Create some hypotheses first!</p>
        </div>
      ) : (
        <>
          {/* Filter Controls */}
          <div className="bg-white shadow rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Project
                </label>
                <select
                  value={filterProject}
                  onChange={(e) => {
                    setFilterProject(e.target.value);
                    setFilterProblem('All'); // Clear problem filter when project changes
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="All">All Projects</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Business Problem
                </label>
                <select
                  value={filterProblem}
                  onChange={(e) => setFilterProblem(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={Object.keys(availableProblems).length === 0}
                >
                  <option value="All">All Problems</option>
                  {Object.entries(availableProblems).map(([id, title]) => (
                    <option key={id} value={id}>{title}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Y-Axis
                </label>
                <select
                  value={yAxis}
                  onChange={(e) => setYAxis(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="business_impact">Business Impact</option>
                  <option value="strategy_alignment">Strategic Alignment</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  X-Axis
                </label>
                <select
                  value={xAxis}
                  onChange={(e) => setXAxis(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="implementation_effort">Implementation Effort (inverse)</option>
                  <option value="technical_feasibility">Technical Feasibility</option>
                </select>
              </div>
            </div>
          </div>

          {filteredHypotheses.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-6 text-center">
              <p className="text-gray-500">No hypotheses match your filter criteria.</p>
            </div>
          ) : (
            <>
              {/* Matrix Visualization */}
              <div className="bg-white shadow rounded-lg mb-6">
                <MatrixVisualization 
                  hypotheses={filteredHypotheses}
                  problems={problemsArray}
                  {...(focusProblemId && { focusProblemId })}
                  showQuadrantLabels={true}
                  showReferences={false}
                  showLegend={false}
                  width="container"
                  height={500}
                  xAxis={xAxis}
                  yAxis={yAxis}
                />
              </div>

              {/* Hypotheses Grouped by Problem */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">
                    Hypotheses ({filteredHypotheses.length})
                  </h2>
                  
                  {(() => {
                    // Group hypotheses by problem
                    const groupedHypotheses = filteredHypotheses.reduce((groups, hypothesis) => {
                      const problemId = hypothesis.problem_id;
                      if (!groups[problemId]) {
                        groups[problemId] = [];
                      }
                      groups[problemId]!.push(hypothesis);
                      return groups;
                    }, {} as Record<string, Hypothesis[]>);

                    // Sort each group by priority score
                    Object.keys(groupedHypotheses).forEach(problemId => {
                      groupedHypotheses[problemId]!.sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
                    });

                    return (
                      <div className="space-y-6">
                        {Object.entries(groupedHypotheses).map(([problemId, hypotheses]) => (
                          <div key={problemId} className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Problem Header */}
                            <div className="bg-indigo-50 px-4 py-4 border-b border-indigo-200">
                              <h3 className="text-base font-semibold text-indigo-900">
                                Problem: {problems[problemId] || 'Unknown Problem'} 
                                <span className="ml-3 text-sm text-indigo-600 font-normal">({hypotheses.length} hypotheses)</span>
                              </h3>
                            </div>
                            
                            {/* Mobile-Responsive Table */}
                            <div className="block lg:hidden space-y-3">
                              {hypotheses.map((hypothesis) => (
                                <div key={hypothesis.id} className="bg-gray-50 rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <h5 className="font-medium text-gray-900 text-sm">{hypothesis.title}</h5>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                      `${getScoreColors(hypothesis.priority_score || 0).background} ${getScoreColors(hypothesis.priority_score || 0).text}`
                                    }`}>
                                      {hypothesis.priority_score?.toFixed(1) || 'N/A'}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3 text-xs">
                                    <div>
                                      <span className="text-gray-500">Impact:</span>
                                      <span className="ml-1 font-medium">{hypothesis.business_impact}/10</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Effort:</span>
                                      <span className="ml-1 font-medium">{hypothesis.implementation_effort}/10</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Strategy:</span>
                                      <span className="ml-1 font-medium">{hypothesis.strategy_alignment}/10</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Feasibility:</span>
                                      <span className="ml-1 font-medium">{hypothesis.technical_feasibility}/10</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Desktop Table */}
                            <div className="hidden lg:block">
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Hypothesis
                                      </th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                        Impact
                                      </th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                        Effort
                                      </th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                        Strategy
                                      </th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                        Feasibility
                                      </th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                                        Priority Score
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {hypotheses.map((hypothesis) => (
                                      <tr key={hypothesis.id} className="hover:bg-gray-50">
                                        <td className="px-3 py-2">
                                          <div className="text-sm font-medium text-gray-900" title={hypothesis.title}>
                                            {hypothesis.title}
                                          </div>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                          <div className="text-sm text-gray-900">{hypothesis.business_impact}/10</div>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                          <div className="text-sm text-gray-900">{hypothesis.implementation_effort}/10</div>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                          <div className="text-sm text-gray-900">{hypothesis.strategy_alignment}/10</div>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                          <div className="text-sm text-gray-900">{hypothesis.technical_feasibility}/10</div>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                            `${getScoreColors(hypothesis.priority_score || 0).background} ${getScoreColors(hypothesis.priority_score || 0).text}`
                                          }`}>
                                            {hypothesis.priority_score?.toFixed(1) || 'N/A'}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MatrixPage;
